import { HOLIDAYS } from "@/constants/holiday";
import dayjs from "dayjs";

/**
 * dayjs.js のフォーマット失敗時の値
 */
export const INVALID_DATE = "Invalid date";

export function getEndOfMonth(year: number, month: number) {
  return dayjs(`${year}-${("0" + month).slice(-2)}`, "YYYY-MM")
    .endOf("month")
    .date();
}

export function fillZero(num: number, digits: number) {
  const zeroText = [...Array(digits).keys()].map(() => "0").join("");
  return `${zeroText}${num}`.slice(-digits);
}

// この関数では"YYYY年MM月DD日"など日本語表記は判定できません。
export function validateDateFormat(date: string, formats: string[]) {
  return formats.some(format => {
    const parsedDate = dayjs(date, format);
    return parsedDate.isValid() && parsedDate.format(format) === date;
  });
}

/**
 * 与えられた日付文字列が指定されたフォーマットのいずれかに一致するかを検証し、
 * 一致する場合はそのフォーマットを返す。一致しない場合はnullを返す。
 * "YYYY年MM月DD日"など日本語表記は判定できません。
 *
 * @param {string} date - 検証する日付の文字列
 * @returns {string | null} - 一致したフォーマット、または一致しない場合はnull
 */
export function validateAndReturnDateFormat(date: string): string | null {
  const matchingFormat = STANDARD_DATE_FORMATS.find(format => {
    const parsedDate = dayjs(date, format, true);
    return parsedDate.isValid() && parsedDate.format(format) === date;
  });
  return matchingFormat || null;
}

/**
 * 指定された日付文字列が、特定のフォーマットに完全に一致するかどうかを検証する。
 * フォーマットはオプショナルで、nullが渡された場合はデフォルトフォーマットを使用する。
 *
 * @param {string} dateStr - 検証する日付の文字列
 * @param {string | null} format - 日付のフォーマット（デフォルトは "YYYY/MM/DD"、null許容）
 * @returns {boolean} - 日付がフォーマットに完全に一致すれば true、そうでなければ false
 */
export const isValidDate = (
  dateStr: string,
  format: string | null = "YYYY/MM/DD"
): boolean => {
  // nullまたはundefinedの場合、デフォルト値を使用。
  const effectiveFormat = format ?? "YYYY/MM/DD";

  const parsedDate = dayjs(dateStr);
  // パースした日付をもう一度同じフォーマットに変換し、それが元の日付文字列と完全に一致するかを検証ています。
  return parsedDate.isValid() && parsedDate.format(effectiveFormat) === dateStr;
};

// dayjsで標準的に処理できる日付フォーマットの配列
export const STANDARD_DATE_FORMATS = [
  "YYYY-MM-DD",
  "YYYY/MM/DD",
  "YYYY/M/D",
  "YYYY/MM/DD HH:mm",
  "YYYY/M/D H:m"
];

export const DATE_FORMATS = [
  "YYYY/MM/DD",
  "YYYY/M/D",
  "YYYY年MM月DD日",
  "YYYY年M月D日"
];

export const DATE_WITH_TIME_FORMATS = [
  "YYYY/MM/DD HH:mm",
  "YYYY/M/D H:m",
  "YYYY年MM月DD日 HH時mm分",
  "YYYY年M月D日 H時m分"
];

export function formatDate(date: string, withTime = false) {
  if (!date) return "";
  // NOTE IE11でのdateのフォーマットエラー回避のため一度replaceしている
  return dayjs(new Date(date.replace(/-/g, "/"))).format(
    withTime ? "YYYY/MM/DD HH:mm" : "YYYY/MM/DD"
  );
}

/**
 * Date型ではない YYYY年MM月DD日 を
 * YYYY/MM/DD HH:mm へ変換する
 */
export const formatDisplayDate = (date: string) => {
  let nextDate = date;
  nextDate = nextDate.replace(/年|月/g, "/");
  nextDate = nextDate.replace(/時/g, ":");
  nextDate = nextDate.replace(/日|分/g, "");

  return nextDate;
};

export function formatDateForYearMonthDay(date: string) {
  if (!date) return "";

  const standardizedDate = date.replace(/-/g, "/");
  // 日付の有効性を確認する。
  if (
    !isValidDate(
      standardizedDate,
      validateAndReturnDateFormat(standardizedDate)
    )
  ) {
    return "Invalid Date";
  }

  const parsedDate = dayjs(standardizedDate);
  return parsedDate.format("YYYY年MM月DD日");
}

/**
 * 開始日〜 終了日”のようにフォーマットするヘルパー関数
 * 20xx/yy/xxという形式の開始・終了日を
 * "20xx年yy月zz日 〜 20xx年yy月zz日”のようにフォーマットする。
 */
export const formatDateRange = (start: string, end: string): string => {
  const startDate = formatDateForYearMonthDay(start);
  const endDate = formatDateForYearMonthDay(end);
  const formattedStartDate = startDate === "Invalid Date" ? "" : startDate;
  const formattedEndDate = endDate === "Invalid Date" ? "" : endDate;

  if (!formattedStartDate && !formattedEndDate) {
    return ""; // 両方とも無効な日付の場合は空文字列を返す
  }

  return `${formattedStartDate} 〜 ${formattedEndDate}`;
};

export function thisYear(short: boolean = false) {
  const now = new Date();
  const currentYear = now.getFullYear();
  const thisYear = now.getMonth() > 2 ? currentYear + 2 : currentYear + 1;
  return short ? Number(String(thisYear).slice(-2)) : thisYear;
}

export function isThisYear(graduateYear: number) {
  return graduateYear === thisYear();
}

/** 数字のみを抽出(卒年度の数字のみ抽出時に使用) */
export const extractYear = (yearStr: string): string | null => {
  const match = yearStr.match(/^\d{4}/);
  return match ? match[0] : null;
};

export function getInitialSelectedChartYears(
  latestYear: number
): [number, number] {
  const now = new Date();
  const month = now.getMonth() + 1;
  const n = latestYear - 1;

  if (4 <= month && month <= 9) {
    return [n - 1, n];
  }
  return [n, n + 1];
}

/** n営業日後の日付を取得 */
export const getBusinessDate = (durationDay: number): dayjs.Dayjs => {
  let businessDate = dayjs();
  let count = 1;

  while (count <= durationDay) {
    businessDate = businessDate.add(1, "day");

    // 土日は営業日とカウントしない
    const day = businessDate.day();
    if ([0, 6].includes(day)) {
      continue;
    }

    // 祝日は営業日とカウントしない
    if (
      HOLIDAYS.some(
        holiday =>
          dayjs(new Date(holiday)).format("YYYY-MM-DD") ===
          businessDate.format("YYYY-MM-DD")
      )
    ) {
      continue;
    }

    count++;
  }

  return businessDate;
};

/**
 * 今日までの日数を取得
 */
export const getDaysToToday = (date: string) => {
  const from = dayjs(date);
  const today = dayjs(Date.now());

  const diffDays = today.diff(from, "days");

  return diffDays;
};

/**
 * 引数の日付が今日以降かを判定
 */
export const isDateBeforeCurrentDate = (datetime: string) => {
  const inputDate = dayjs(datetime);
  const now = dayjs();
  return inputDate.isBefore(now, "second");
};

/**
 * inputDateがspecificDateより未来の場合true
 */
export const isDateAfterSpecificDate = (
  inputDate: string,
  specificDate: string
) => {
  const inputDateTime = dayjs(inputDate);
  const specificDateTime = dayjs(specificDate);
  return inputDateTime.isAfter(specificDateTime);
};

export const isSameDateAndMinute = (startDate: string, endDate: string) => {
  const startDateTime = dayjs(startDate);
  const endDateTime = dayjs(endDate);
  return startDateTime.isSame(endDateTime, "minute");
};

export const unixTimeMSToDateStr = (unixTimeMS: number) => {
  return dayjs(unixTimeMS).format("YYYY/MM/DD");
};

export const unixTimeMsToDateStrJPFormat = (unixTimeMS: number) => {
  return dayjs(unixTimeMS).format("YYYY年MM月DD日");
};

export const unixTimeMSToDateOrTimeStr = (unixTimeMS: number) => {
  const currentDate = dayjs();
  const inputDate = dayjs(unixTimeMS);

  if (currentDate.isSame(inputDate, "day")) {
    return inputDate.format("HH:mm");
  } else {
    return inputDate.format("MM/DD");
  }
};

export const unixTimeMSToTimeStr = (unixTimeMS: number) => {
  const inputDate = dayjs(unixTimeMS);

  return inputDate.format("HH:mm");
};
